import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BuildingDetailsLampsAddLamp from './BuildingDetailsLampsAddLamp';

const BuildingDetailsLamps = () => {
    const { id } = useParams();
    const [lamps, setLamps] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [editLamp, setEditLamp] = useState(null);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    const fetchLamps = () => {
        axios.get(`/buildings/${id}/lamps/`)
            .then(res => setLamps(res.data))
            .catch(() => setSnackbar({ open: true, message: 'Failed to fetch lamps.', severity: 'error' }));
    };

    useEffect(() => {
        fetchLamps();
    }, [id]);

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => {
        setModalOpen(false);
        setEditLamp(null);
    };

    const handleSuccess = (actionType) => {
        fetchLamps();
        const message = actionType === 'delete'
            ? 'Lamp deleted successfully!'
            : editLamp
                ? 'Lamp updated successfully!'
                : 'Lamp added successfully!';
        setSnackbar({ open: true, message, severity: 'success' });
    };

    const handleError = () => {
        setSnackbar({ open: true, message: 'Failed to save lamp.', severity: 'error' });
    };

    const handleDelete = (lampId) => {
        axios.delete(`/buildings/${id}/lamps/${lampId}/`)
            .then(() => handleSuccess('delete'))
            .catch(() => handleError());
    };

    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Lighting</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleModalOpen}>
                    Add Lamp
                </Button>
            </Box>

            {lamps
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((lamp) => (
                    <Accordion
                        key={lamp.id}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            boxShadow: 'none',
                            border: '1px solid #e0e0e0',
                            '&::before': { display: 'none' }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                                {lamp.lamp_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditLamp(lamp);
                                    setModalOpen(true);
                                }}
                            >Edit</Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ mr: 2 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(lamp.id);
                                }}
                            >Delete</Button>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {Object.entries(lamp).map(([key, value]) => {
                                    if (key === 'id' || key === 'building' || value === null || value === '') return null;

                                    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                                    return (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="subtitle2">{label}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                    count={Math.ceil(lamps.length / itemsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                />
            </Box>

            <Dialog open={modalOpen} onClose={handleModalClose}>
                <BuildingDetailsLampsAddLamp
                    buildingId={id}
                    lamp={editLamp}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    onClose={handleModalClose}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuildingDetailsLamps;